import Image from "next/image";
import React from "react";
import PinIcon from "@/public/icons/pin.svg";
import { MoveRight } from "lucide-react";
import { DevelopmentCardData, DevelopmentRibbonType } from "./types";

const getRibbonSource = (ribbonType: DevelopmentRibbonType): string | null => {
  switch (ribbonType) {
    case "onlyThreeLeft":
      return "/icons/only3left.png";
    case "onlyTwoLeft":
      return "/icons/only2left.png";
    case "lastUnit":
      return "/icons/lastUnit.png";
    case "comingSoon":
      return "/icons/new-coming-soon-ribbon.png";
    case "justAdded":
      return "/icons/just-added.png";
    case "soldOut":
      return "/icons/soldOut.png";
  }
};

const getCtaText = (homesAvailable: number | null, ribbon?: DevelopmentRibbonType): string => {
  if (ribbon === "comingSoon" || homesAvailable === null) return "See details";
  if (ribbon === "soldOut" || homesAvailable === 0) return "Sold out";
  if (homesAvailable === 1) return "1 home available";
  return `${homesAvailable} homes available`;
};

const DevelopmentCard = ({ development }: { development: DevelopmentCardData }) => {
  const { image, location, name, description, homesAvailable, ribbon, href } = development;
  const ribbonSrc = ribbon ? getRibbonSource(ribbon) : null;

  return (
    <div className="rounded-2xl shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 bg-white h-full flex">
      <a
        href={href}
        className="w-full h-full no-underline flex flex-col"
        style={{ WebkitTapHighlightColor: "transparent" }}
        aria-label={`View details for ${name}`}
      >
        <div className="relative rounded-tl-[15px] rounded-tr-[15px] overflow-hidden">
          <div className="w-full pb-[66.6667%] relative">
            {image ? (
              <Image
                src={image}
                alt={name}
                className="rounded-tl-[15px] rounded-tr-[15px]"
                layout="fill"
                objectFit="cover"
                loading="eager"
              />
            ) : (
              <div className="absolute inset-0 bg-landing-blue rounded-tl-[15px] rounded-tr-[15px]" />
            )}
          </div>
          {ribbonSrc && (
            <div className="absolute top-0 left-0 w-1/2 h-2/3">
              <Image
                src={ribbonSrc}
                alt={ribbon as string}
                layout="fill"
                className="object-contain object-left-top"
              />
            </div>
          )}
        </div>
        <div className="flex flex-col p-5 gap-[16px] font-sans grow justify-between">
          <div className="flex flex-col gap-[16px]">
            <div className="bg-[linear-gradient(270deg,rgba(243,146,55,0.2)_3.04%,rgba(255,171,0,0.2)_96.96%)] h-[26px] inline-flex self-start items-center gap-1 px-2 py-1 rounded-[5px]">
              <PinIcon className="h-[14px] w-[11px] fill-landing-orange" />
              <p className="font-sans font-semibold text-navy text-xs leading-[18px] line-clamp-1">
                {location}
              </p>
            </div>
            <p className="text-landing-blue font-semibold text-2xl">{name}</p>
            <p className="text-landing-blue text-lg font-normal leading-[26px]">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-landing-orange text-lg font-semibold leading-normal">
              {getCtaText(homesAvailable, ribbon)}
            </p>
            <MoveRight stroke="#F39237" strokeWidth={1.125} />
          </div>
        </div>
      </a>
    </div>
  );
};

export default DevelopmentCard;
